import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { LogoShield } from '@/components/LogoShield';
import { MfaEnrollment } from '@/components/portal/MfaEnrollment';
import { ViewServiceAgreement } from '@/components/portal/ViewServiceAgreement';
import { Helmet } from '@/components/Helmet';
import { toast } from 'sonner';
import { SERVICE_AGREEMENT_VERSION } from '@/lib/serviceAgreement';

type OnboardingStep = 'password' | 'agreement' | 'mfa' | 'complete';

type InviteOnboardingStatus = {
  success: boolean;
  invite: {
    id: string;
    email: string;
    status: string;
    expiresAt: string;
    acceptedAt: string | null;
    accountKind: 'standard' | 'dev';
    devPriceCents: number | null;
    devPriceCurrency: string | null;
    devPriceId: string | null;
  };
  profile: {
    userId: string;
    email: string | null;
    accountKind: 'standard' | 'dev';
    onboardingStatus: string;
    serviceAgreementAcceptedAt: string | null;
    serviceAgreementVersion: string | null;
    mfaMethod: string | null;
  };
  step: OnboardingStep;
};

const passwordSchema = z.string().min(8, 'Password must be at least 8 characters');
const INVITE_ID_STORAGE_KEY = 'invite_onboarding_invite_id';
const NEXT_PATH_STORAGE_KEY = 'invite_onboarding_next_path';

export default function InviteOnboarding() {
  const { user, loading, markSessionMfaVerified, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [status, setStatus] = useState<InviteOnboardingStatus | null>(null);
  const [savingPassword, setSavingPassword] = useState(false);
  const [acceptingAgreement, setAcceptingAgreement] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreementChecked, setAgreementChecked] = useState(false);

  const inviteIdFromUrl = searchParams.get('invite_id');
  const nextPathFromUrl = searchParams.get('next');
  const inviteId = useMemo(() => {
    const fromStorage = typeof window !== 'undefined' ? localStorage.getItem(INVITE_ID_STORAGE_KEY) : null;
    return inviteIdFromUrl || fromStorage || '';
  }, [inviteIdFromUrl]);
  const nextPath = useMemo(() => {
    const fromStorage = typeof window !== 'undefined' ? localStorage.getItem(NEXT_PATH_STORAGE_KEY) : null;
    return sanitizeNextPath(nextPathFromUrl || fromStorage || '');
  }, [nextPathFromUrl]);

  useEffect(() => {
    if (inviteIdFromUrl) {
      localStorage.setItem(INVITE_ID_STORAGE_KEY, inviteIdFromUrl);
    }
  }, [inviteIdFromUrl]);

  useEffect(() => {
    if (nextPathFromUrl) {
      const safePath = sanitizeNextPath(nextPathFromUrl);
      if (safePath) {
        localStorage.setItem(NEXT_PATH_STORAGE_KEY, safePath);
      }
    }
  }, [nextPathFromUrl]);

  const loadStatus = useCallback(async () => {
    if (!user) return;
    setStatusLoading(true);
    setStatusError(null);

    try {
      const { data, error } = await supabase.functions.invoke('invite-onboarding', {
        body: {
          action: 'status',
          inviteId: inviteId || undefined,
        },
      });
      if (error || data?.error) {
        throw new Error(error?.message || data?.error || 'Unable to load onboarding status.');
      }

      const next = data as InviteOnboardingStatus;
      setStatus(next);
      if (next?.invite?.id) {
        localStorage.setItem(INVITE_ID_STORAGE_KEY, next.invite.id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load onboarding state.';
      setStatusError(message);
    } finally {
      setStatusLoading(false);
    }
  }, [inviteId, user]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/portal/login', { replace: true });
      return;
    }
    void loadStatus();
  }, [loading, loadStatus, navigate, user]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSavingPassword(true);
    try {
      passwordSchema.parse(password);
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match.');
      }

      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ onboarding_status: 'password_set' })
        .eq('user_id', user.id);

      if (profileError) {
        throw profileError;
      }

      setPassword('');
      setConfirmPassword('');
      toast.success('Password set. Continue onboarding.');
      await loadStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to set password.';
      toast.error(message);
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAcceptAgreement = async () => {
    if (!agreementChecked) {
      toast.error('You must accept the service agreement to continue.');
      return;
    }
    setAcceptingAgreement(true);
    try {
      const { data, error } = await supabase.functions.invoke('invite-onboarding', {
        body: {
          action: 'accept-agreement',
          inviteId: status?.invite?.id || inviteId || undefined,
          serviceAgreementVersion: SERVICE_AGREEMENT_VERSION,
        },
      });
      if (error || data?.error) {
        throw new Error(error?.message || data?.error || 'Unable to save agreement acceptance.');
      }
      toast.success('Service agreement accepted.');
      await loadStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to accept agreement.';
      toast.error(message);
    } finally {
      setAcceptingAgreement(false);
    }
  };

  const handleCompleteOnboarding = async () => {
    setCompleting(true);
    try {
      const { data, error } = await supabase.functions.invoke('invite-onboarding', {
        body: {
          action: 'complete',
          inviteId: status?.invite?.id || inviteId || undefined,
        },
      });
      if (error || data?.error) {
        throw new Error(error?.message || data?.error || 'Unable to finalize onboarding.');
      }
      markSessionMfaVerified();
      localStorage.removeItem(INVITE_ID_STORAGE_KEY);
      localStorage.removeItem(NEXT_PATH_STORAGE_KEY);
      navigate(nextPath || '/portal', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to complete onboarding.';
      toast.error(message);
    } finally {
      setCompleting(false);
    }
  };

  const handleMfaComplete = async () => {
    await loadStatus();
    await handleCompleteOnboarding();
  };

  if (loading || statusLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Invite Onboarding | ShadowScan</title>
          <meta
            name="description"
            content="Complete invite onboarding with password setup, agreement acceptance, and MFA enrollment."
          />
        </Helmet>
        <div className="text-primary animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!user) return null;

  if (statusError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg cyber-card">
          <CardHeader>
            <div className="flex items-center justify-center gap-2 mb-2">
              <LogoShield className="h-7 w-7" />
              <span className="text-xl font-mono font-bold text-foreground">ShadowScan</span>
            </div>
            <CardTitle>Invite Onboarding Unavailable</CardTitle>
            <CardDescription>{statusError}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Button onClick={() => void loadStatus()}>Retry</Button>
            <Button variant="outline" onClick={async () => { await signOut(); navigate('/portal/login', { replace: true }); }}>
              Return to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!status) return null;

  if (status.step === 'mfa') {
    return (
      <>
        <Helmet>
          <title>Enroll MFA | ShadowScan Invite Onboarding</title>
          <meta
            name="description"
            content="Enroll MFA to complete your invite onboarding and secure your ShadowScan account."
          />
        </Helmet>
        <MfaEnrollment
          userId={status.profile.userId}
          email={status.profile.email || status.invite.email}
          existingMethods={status.profile.mfaMethod ? status.profile.mfaMethod.split(',').map((x) => x.trim()).filter(Boolean) : []}
          onComplete={() => void handleMfaComplete()}
        />
      </>
    );
  }

  if (status.step === 'complete') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg cyber-card">
          <CardHeader>
            <div className="flex items-center justify-center gap-2 mb-2">
              <LogoShield className="h-7 w-7" />
              <span className="text-xl font-mono font-bold text-foreground">ShadowScan</span>
            </div>
            <CardTitle>Onboarding Complete</CardTitle>
            <CardDescription>Your invite setup is complete. Continue to your portal dashboard.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => void handleCompleteOnboarding()} disabled={completing}>
              {completing ? 'Finishing...' : 'Continue to Portal'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Helmet>
        <title>Invite Onboarding | ShadowScan</title>
        <meta
          name="description"
          content="Complete invite onboarding with password setup, service agreement acceptance, and MFA enrollment."
        />
      </Helmet>
      <Card className="w-full max-w-xl cyber-card">
        <CardHeader>
          <div className="flex items-center justify-center gap-2 mb-2">
            <LogoShield className="h-7 w-7" />
            <span className="text-xl font-mono font-bold text-foreground">ShadowScan</span>
          </div>
          <CardTitle>
            {status.step === 'password' ? 'Set Your Password' : 'Accept Service Agreement'}
          </CardTitle>
          <CardDescription>
            {status.step === 'password'
              ? 'Create a password for your invited account before continuing.'
              : 'Review and accept the current service agreement before MFA enrollment.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status.step === 'password' ? (
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a secure password"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={savingPassword}>
                {savingPassword ? 'Saving...' : 'Save Password'}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Account type</span>
                  <span className="font-medium capitalize">{status.invite.accountKind}</span>
                </div>
                {status.invite.accountKind === 'dev' && typeof status.invite.devPriceCents === 'number' && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-muted-foreground">Default dev price</span>
                    <span className="font-medium">
                      {(status.invite.devPriceCurrency || 'AUD')} ${(status.invite.devPriceCents / 100).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <ViewServiceAgreement
                  trigger={<Button variant="outline" className="w-full">Read Service Agreement</Button>}
                />
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="agreement-accept"
                  checked={agreementChecked}
                  onCheckedChange={(checked) => setAgreementChecked(checked === true)}
                />
                <label htmlFor="agreement-accept" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                  I accept the ShadowScan Security Services Agreement (version {SERVICE_AGREEMENT_VERSION}).
                </label>
              </div>
              <Button className="w-full" disabled={acceptingAgreement || !agreementChecked} onClick={handleAcceptAgreement}>
                {acceptingAgreement ? 'Saving...' : 'Accept and Continue'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function sanitizeNextPath(value: string) {
  const trimmed = value.trim();
  if (!trimmed.startsWith('/')) return '';
  if (trimmed.startsWith('//')) return '';
  return trimmed;
}
